import { useCallback } from 'react';
import * as Yup from 'yup';
import { MessageParams } from 'yup/lib/types';
import { I18n } from 'next-i18next';
import { ValidateResult } from 'react-hook-form';
import booking from './yup/schema/BookingSchema';
import credential from './yup/schema/CredentialSchema';
import customer from './yup/schema/CustomerSchema';

type MessageType = {
  path: string;
  label: string;
};

// @ts-expect-error extend the Yup object
Yup.booking = booking;
// @ts-expect-error extend the Yup object
Yup.customer = customer;
// @ts-expect-error extend the Yup object
Yup.credential = credential;

Yup.setLocale({
  mixed: {
    required: () => 'common.required',
  },
  string: {
    max: (parameter: MessageParams & { max: number }) => ({
      path: 'common.max',
      max: parameter.max,
    }),
    min: (parameter: MessageParams & { min: number }) => ({
      path: 'common.min',
      min: parameter.min,
    }),
    matches: (parameter: MessageParams & { regex: RegExp }) => ({
      path: 'cognito.should-be-email',
      format: parameter.regex,
    }),
  },
  number: {
    max: (parameter: MessageParams & { max: number }) => ({
      path: 'common.max',
      max: parameter.max,
    }),
    min: (parameter: MessageParams & { min: number }) => ({
      path: 'common.min',
      min: parameter.min,
    }),
  },
});

Yup.addMethod<Yup.StringSchema>(Yup.string, 'alphanumeric', (message) => {
  return Yup.string().matches(/^[a-zA-Z0-9]*$/, () => message || 'common.alphanumeric');
});

Yup.addMethod<Yup.StringSchema>(Yup.string, 'numeric', (message) => {
  return Yup.string().matches(/^[0-9]*$/, () => message || 'common.numeric');
});

export const useYupValidationResolver = (validationSchema: Yup.BaseSchema) =>
  useCallback(
    async (data: unknown) => {
      try {
        const values = await validationSchema.validate(data, {
          abortEarly: false,
        });
        return {
          values,
          errors: {},
        };
      } catch (errors) {
        return {
          values: {},
          errors: (errors as Yup.ValidationError).inner.reduce(
            (
              accumulator: { [key: string]: { types: Record<string, string> } },
              current: Yup.ValidationError
            ) => {
              if (current?.path) {
                return {
                  ...accumulator,
                  [current?.path]: {
                    types: {
                      ...accumulator[current?.path]?.types,
                      [current.type ?? 'validation']: current.message,
                    },
                  },
                } as { [key: string]: { types: Record<string, string> } };
              }
              return accumulator;
            },
            {}
          ),
        };
      }
    },
    [validationSchema]
  );

export const displayError = (
  message: ValidateResult | MessageType,
  i18n: I18n,
  t: (key: string, options?: unknown) => string
) => {
  if (typeof message === 'object') {
    return i18n?.exists(`error:${(message as MessageType)?.path}`)
      ? t(`error:${(message as MessageType)?.path}`, { ...message })
      : (message as MessageType)?.path;
  }
  return i18n?.exists(`error:${message}`) ? t(`error:${message}`) : message;
};

export default Yup;
